import type { ReactNode } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AssetType,
  Badge,
  Button,
  Card,
  cn,
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
  type IBadgeProps,
  type IconType,
  type TAssetTypeVariant,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ChatActivity } from '@/shared/components/ChatActivity';

import type {
  TRainToolApprovalDecision,
  TRainToolCallStatus,
} from '../../components/RainToolCallCard';

/**
 * One row of the proposal, already in the reader's vocabulary: the agent
 * profile resolves the type's icon and label from whatever the product calls
 * that type, so the card never has its own copy of that mapping.
 */
export interface IRainApprovalField {
  id: string;
  name: string;
  typeIcon: IconType;
  typeLabel: string;
  required: boolean;
  description?: string;
  detail?: string;
}

/** The collection being created: its whole schema is new. */
export interface IRainCollectionApproval {
  collectionName: string;
  fieldCountLabel: string;
  fields: IRainApprovalField[];
  /**
   * Replaces the name and the icon when nothing was applied. The frame names a
   * thing and wears its icon, which asserts that the thing exists — true once
   * the call succeeds, a lie for one that was refused or failed.
   */
  proposalHeading?: string;
}

export type TRainApprovalChangeKind = 'added' | 'changed' | 'removed';

/** One entry a change adds, edits or drops. */
export interface IRainApprovalChange {
  id: string;
  name: string;
  /**
   * What is happening to the entry. The kind picks the badge's colour and
   * strikes the name through; the word on it is the agent's to choose. Absent
   * on a plain listing, where nothing is happening to the entry at all.
   */
  badge?: { label: string; kind: TRainApprovalChangeKind };
  icon?: IconType;
  /** Second line: the entry's type, or what about it changed. */
  detail?: string;
}

/**
 * A set being replaced, shown as what that changes rather than as the new set —
 * the entries that stay the same are the ones the reader can skip. Serves any
 * tool that PUTs a whole list, so a name omitted from it is dropped.
 */
export interface IRainChangeSetApproval {
  name: string;
  icon: IconType;
  /** Counts of what changes, e.g. "1 removed · 2 added". */
  changeSummary: string;
  changes: IRainApprovalChange[];
  /** The entries left alone, collapsed to a count. */
  unchangedLabel?: string;
  /** As on a collection: replaces the name and icon when nothing was applied. */
  proposalHeading?: string;
}

/** The asset being registered. */
export interface IRainAssetApproval {
  name: string;
  /** Secondary line. The real mime type and size only exist once it does. */
  metadata: string;
  assetType: TAssetTypeVariant;
  /** Labelled so a lone badge does not read as a tab. */
  destinationLabel: string;
  destination: string;
  /** Says the file was only ever proposed, when it was never saved. */
  proposalHeading?: string;
}

export type TRainApprovalContent =
  | ({ kind: 'collection' } & IRainCollectionApproval)
  | ({ kind: 'changes' } & IRainChangeSetApproval)
  | ({ kind: 'asset' } & IRainAssetApproval);

/** The cost of approving, said plainly above the API's own words. */
export interface IRainApprovalWarning {
  title: string;
  description: string;
}

/**
 * Everything the card shows, as finished display strings. The agent profile
 * builds this from a tool call, so the card stays presentational and the copy
 * stays with the agent that owns the tool.
 */
export interface IRainApprovalRequest {
  /** What quill is asking for, while the decision is open. */
  pendingTitle: string;
  /** What approving would do. Only shown while the decision is open. */
  pendingDescription: string;
  /** Names the action rather than agreeing with it, so it reads alone. */
  approveLabel: string;
  denyLabel: string;
  /**
   * One line each for how it ended, which becomes the card's whole header.
   * Approving only starts the change — it can still fail — so a run and a
   * failure need their own wording.
   */
  runningSummary: string;
  approvedSummary: string;
  failedSummary: string;
  /** Said plainly, above the API's own words. */
  failedTitle: string;
  failedDescription: string;
  deniedSummary: string;
  /**
   * What approving costs, when it costs something irreversible. Shown with the
   * decision and nowhere else: once it is answered the warning is spent.
   */
  warning?: IRainApprovalWarning;
  /**
   * Confirms with the DS's destructive button, the way a delete dialog does,
   * when approving deletes something.
   */
  isDestructive?: boolean;
  resourceLinkLabel?: string;
  /** In-app path to what was created. Absent until it exists. */
  resourceHref?: string;
  content: TRainApprovalContent;
}

export interface IRainApprovalCardProps {
  request: IRainApprovalRequest;
  decision?: TRainToolApprovalDecision;
  /** The call's own outcome, which an approval only sets in motion. */
  status?: TRainToolCallStatus;
  denialReason?: string;
  /** Why the change failed after it was approved. */
  errorMessage?: string;
  isSubmitting?: boolean;
  onApprove?: () => void;
  /**
   * Refusing sends no reason: the composer is a better place to say why than a
   * second text box inside the message, and quill asks once it is refused.
   */
  onDeny?: () => void;
}

function IconChip({ icon }: { icon: IconType }) {
  return (
    <Flex
      align="center"
      justify="center"
      className="size-6 shrink-0 rounded-sm bg-muted text-muted-foreground"
    >
      <Icon type={icon} size="xs" />
    </Flex>
  );
}

/** A heading and a count, above the rows the proposal is made of. */
function ProposalFrame({
  name,
  meta,
  icon,
  children,
}: {
  name: string;
  meta: string;
  /** Absent when the frame heads a proposal rather than a real collection. */
  icon?: IconType;
  children: ReactNode;
}) {
  return (
    <Flex
      direction="column"
      overflow="hidden"
      width="full"
      className="min-w-0 rounded-lg border border-border"
    >
      <Flex
        align="center"
        gap={2}
        width="full"
        className="bg-muted px-3 py-2.5"
      >
        {icon && <IconChip icon={icon} />}
        <Text level="small" weight="medium" className="min-w-0 flex-1 truncate">
          {name}
        </Text>
        <Text as="span" level="xSmall" color="muted" className="shrink-0">
          {meta}
        </Text>
      </Flex>
      {/* Fixed layout so a long unbreakable name truncates in its cell. The
          DS wraps every table in `overflow-x-auto`, and an auto-layout table
          widens to fit instead, which scrolls the row sideways. */}
      <Table className="table-fixed">
        <TableBody>{children}</TableBody>
      </Table>
    </Flex>
  );
}

/** The proposed schema, in the same shape the Library's schema tab uses. */
export function RainSchemaPreview({
  content,
}: {
  content: IRainCollectionApproval;
}) {
  const { t } = useTranslation('automation');

  return (
    <ProposalFrame
      name={content.proposalHeading ?? content.collectionName}
      meta={content.fieldCountLabel}
      icon={content.proposalHeading ? undefined : 'Database'}
    >
      {content.fields.map(field => (
        <TableRow key={field.id}>
          <TableCell className="min-w-0 whitespace-normal">
            <Flex align="center" gap={2} className="min-w-0">
              <IconChip icon={field.typeIcon} />
              <Flex
                direction="column"
                gap={1}
                className="min-w-0 flex-1 break-words"
              >
                <Text as="span" level="small" className="break-words">
                  {field.name}
                </Text>
                {field.description && (
                  <Text level="xSmall" color="muted">
                    {field.description}
                  </Text>
                )}
                {field.detail && (
                  <Text level="xSmall" color="muted">
                    {field.detail}
                  </Text>
                )}
              </Flex>
            </Flex>
          </TableCell>
          <TableCell className="w-20">
            <Text as="span" level="xSmall" color="muted">
              {field.typeLabel}
            </Text>
          </TableCell>
          <TableCell className="w-20 text-right">
            <Text as="span" level="xSmall" color="muted">
              {t(
                field.required
                  ? 'automation.quill2.approval.field.required'
                  : 'automation.quill2.approval.field.optional',
              )}
            </Text>
          </TableCell>
        </TableRow>
      ))}
    </ProposalFrame>
  );
}

/** Only the removal undoes something, so only the removal is loud. */
const CHANGE_VARIANT: Readonly<
  Record<TRainApprovalChangeKind, IBadgeProps['variant']>
> = {
  removed: 'destructive',
  changed: 'warning',
  added: 'secondary',
};

/**
 * A replaced set, as the changes it makes. Two columns rather than the three a
 * full schema gets: the detail line is prose ("Text → Number"), which would be
 * truncated to nothing in a column narrow enough to fit beside a badge.
 */
function ChangeSetProposal({ content }: { content: IRainChangeSetApproval }) {
  return (
    <ProposalFrame
      name={content.proposalHeading ?? content.name}
      meta={content.changeSummary}
      icon={content.proposalHeading ? undefined : content.icon}
    >
      {content.changes.map(change => (
        <TableRow key={change.id}>
          <TableCell className="min-w-0">
            <Flex align="center" gap={2} className="min-w-0">
              {change.icon && <IconChip icon={change.icon} />}
              <Flex direction="column" className="min-w-0">
                <Text
                  as="span"
                  level="small"
                  className={cn(
                    'truncate',
                    // The entry itself is going away, not just its contents.
                    change.badge?.kind === 'removed' && 'line-through',
                  )}
                >
                  {change.name}
                </Text>
                {change.detail && (
                  <Text
                    as="span"
                    level="xSmall"
                    color="muted"
                    className="truncate"
                  >
                    {change.detail}
                  </Text>
                )}
              </Flex>
            </Flex>
          </TableCell>
          <TableCell className="w-24 text-right">
            {change.badge && (
              <Badge variant={CHANGE_VARIANT[change.badge.kind]}>
                {change.badge.label}
              </Badge>
            )}
          </TableCell>
        </TableRow>
      ))}
      {content.unchangedLabel && (
        <TableRow>
          <TableCell colSpan={2}>
            <Text as="span" level="xSmall" color="muted">
              {content.unchangedLabel}
            </Text>
          </TableCell>
        </TableRow>
      )}
    </ProposalFrame>
  );
}

/**
 * The proposed asset, then where it lands. Stacked rather than side by side:
 * the design's two-column row relies on a `sm:` breakpoint, and this renders in
 * a resizable panel whose width never tracks the viewport.
 */
function AssetProposal({ content }: { content: IRainAssetApproval }) {
  return (
    <Flex
      direction="column"
      gap={3}
      width="full"
      className="min-w-0 rounded-lg border border-border bg-muted p-3"
    >
      {content.proposalHeading && (
        <Text level="small" weight="medium" className="min-w-0 truncate">
          {content.proposalHeading}
        </Text>
      )}
      <Flex align="center" gap={3} width="full" className="min-w-0">
        <AssetType
          type={content.assetType}
          iconSize="md"
          className="shrink-0"
        />
        <Flex direction="column" className="min-w-0 flex-1">
          <Text level="small" weight="medium" className="truncate">
            {content.name}
          </Text>
          <Text as="span" level="xSmall" color="muted" className="truncate">
            {content.metadata}
          </Text>
        </Flex>
      </Flex>
      <Flex align="center" gap={2} className="min-w-0">
        <Text as="span" level="xSmall" color="muted" className="shrink-0">
          {content.destinationLabel}
        </Text>
        <Badge variant="secondary" className="min-w-0 gap-1">
          <Icon type="Folder" size="xs" />
          {/* `text-inherit` so the badge variant keeps owning the colour. */}
          <Text as="span" level="xSmall" className="truncate text-inherit">
            {content.destination}
          </Text>
        </Badge>
      </Flex>
    </Flex>
  );
}

function ApprovalProposal({ content }: { content: TRainApprovalContent }) {
  switch (content.kind) {
    case 'collection':
      return <RainSchemaPreview content={content} />;
    case 'changes':
      return <ChangeSetProposal content={content} />;
    case 'asset':
      return <AssetProposal content={content} />;
  }
}

function ResourceLink({ request }: { request: IRainApprovalRequest }) {
  if (!request.resourceLinkLabel || !request.resourceHref) return null;

  return (
    <Flex className="pl-6">
      <Button variant="outline" size="xs" asChild>
        <Link to={request.resourceHref}>
          {request.resourceLinkLabel}
          <Icon type="ArrowUpRight" size="xs" />
        </Link>
      </Button>
    </Flex>
  );
}

/**
 * A tool call the agent loop is paused on, waiting for the user to approve a
 * durable change to their workspace.
 *
 * Two presentations, because they are two different things. Open, it is a card
 * like the other tool cards: the ask, the proposal, then the decision under it.
 * Resolved, it is one past-tense line like every other tool call, with the
 * proposal kept behind the standard disclosure.
 */
export function RainApprovalCard({
  request,
  decision,
  status,
  denialReason,
  errorMessage,
  isSubmitting = false,
  onApprove,
  onDeny,
}: IRainApprovalCardProps) {
  const { t } = useTranslation('automation');

  // A terminal status resolves the card even with no decision on record:
  // approval gating is the agent's config, not this card's, so a call that runs
  // without ever asking must not sit in the transcript still asking.
  if (decision || status === 'success' || status === 'error') {
    // Four endings, not two: approving only starts the change, so it can still
    // be running, and it can still fail.
    const isDenied = decision === 'denied';
    const hasFailed = !isDenied && status === 'error';
    const isRunning = !isDenied && status === 'running';

    let icon: IconType = 'Check';
    let title = request.approvedSummary;
    if (isDenied) {
      icon = 'X';
      title = request.deniedSummary;
    } else if (hasFailed) {
      icon = 'CircleAlert';
      title = request.failedSummary;
    } else if (isRunning) {
      icon = 'Loader2';
      title = request.runningSummary;
    }

    return (
      <ChatActivity
        variant="tool-call"
        icon={icon}
        title={title}
        isActive={isRunning}
        isError={hasFailed}
        collapsible
        data-testid="rain-approval-card"
        data-decision={decision}
        data-status={status ?? ''}
        statusContent={
          <>
            {isDenied && denialReason && (
              <Text
                as="span"
                level="xSmall"
                color="muted"
                className="min-w-0 pl-6"
              >
                {t('automation.quill2.approval.deniedReason', {
                  reason: denialReason,
                })}
              </Text>
            )}
            {!isDenied && !hasFailed && !isRunning && (
              <ResourceLink request={request} />
            )}
          </>
        }
      >
        <Flex direction="column" gap={2} width="full" className="min-w-0">
          {hasFailed && (
            // The raw text is subordinate, not the headline: it is a flattened
            // API error, and quill's own prose has been seen to misread a
            // failure as a refusal — so the card says plainly what happened.
            <Alert variant="destructive">
              <Icon type="CircleAlert" />
              {/* `AlertTitle` is line-clamped to one line by the DS, so the
                  headline stays short and the rest goes below it. */}
              <AlertTitle>{request.failedTitle}</AlertTitle>
              <AlertDescription>
                <Text as="span" level="xSmall">
                  {request.failedDescription}
                </Text>
                {errorMessage && (
                  <Text as="span" level="xSmall" color="muted">
                    {errorMessage}
                  </Text>
                )}
              </AlertDescription>
            </Alert>
          )}
          <ApprovalProposal content={request.content} />
        </Flex>
      </ChatActivity>
    );
  }

  const canDecide = onApprove || onDeny;

  return (
    <Card
      className="w-full min-w-0 gap-3 p-3"
      data-testid="rain-approval-card"
      data-decision="awaiting"
    >
      <Flex direction="column" gap={1} width="full" className="min-w-0">
        <Flex align="center" gap={2} className="min-w-0">
          <Icon
            type="CircleHelp"
            size="sm"
            className="shrink-0 text-muted-foreground"
          />
          <Text level="small" weight="medium" className="min-w-0">
            {request.pendingTitle}
          </Text>
        </Flex>
        <Text as="span" level="xSmall" color="muted">
          {request.pendingDescription}
        </Text>
      </Flex>

      <ApprovalProposal content={request.content} />

      {request.warning && (
        <Alert variant="destructive">
          <Icon type="TriangleAlert" />
          {/* `AlertTitle` is line-clamped to one line by the DS, so the
              headline stays short and the consequence goes below it. */}
          <AlertTitle>{request.warning.title}</AlertTitle>
          <AlertDescription>
            <Text as="span" level="xSmall">
              {request.warning.description}
            </Text>
          </AlertDescription>
        </Alert>
      )}

      {canDecide && (
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            variant={request.isDestructive ? 'destructive' : 'default'}
            onClick={() => onApprove?.()}
            // Neither button spins: `isSubmitting` does not say which one was
            // pressed, and a spinner on the confirm button after a refusal
            // reads as though the change went through.
            disabled={isSubmitting || !onApprove}
            data-testid="rain-approval-approve"
          >
            {request.approveLabel}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDeny?.()}
            disabled={isSubmitting || !onDeny}
            data-testid="rain-approval-deny"
          >
            {request.denyLabel}
          </Button>
        </Flex>
      )}
    </Card>
  );
}
